import { useEffect, useState } from 'react';
import {
    Grid,
    TextField,
    Button,
    Typography,
    Divider,
    Paper,
    List,
    ListItem,
    ListItemText,
    IconButton,
    FormGroup,
    FormControlLabel,
    Checkbox,
    Collapse,
    Box,
    Card,
    CardContent,
    CardActions
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useTranslation } from 'react-i18next';
import ExerciseBuilder from './ExerciseBuilder';

const AEROBIC_TYPES = [
    'rehabilitation',
    'treadmill',
    'ergometer',
    'walking',
    'running',
    'cycling',
    'rest',
    'other'
];

const RESISTANCE_TYPES = [
    'squat',
    'sit_to_stand',
    'push',
    'pull',
    'arm_raise',
    'knee_extension'
];

const SessionBuilder = ({
    onAdd,
    onUpdate,
    existingSession,
    onCancelEdit,
    inlineMode = false
}) => {
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [hour, setHour] = useState('');
    const [minute, setMinute] = useState('');
    const [days, setDays] = useState([]);
    const [exercises, setExercises] = useState([]);
    const [showExerciseForm, setShowExerciseForm] = useState(false);
    const [errors, setErrors] = useState({});
    const [dragOverIndex, setDragOverIndex] = useState(null);
    const { t } = useTranslation();

    const TYPE_LABELS = {
        treadmill: t('exercise.types.treadmill'),
        ergometer: t('exercise.types.ergometer'),
        walking: t('exercise.types.walking'),
        running: t('exercise.types.running'),
        cycling: t('exercise.types.cycling'),
        rest: t('exercise.types.rest'),
        easy: t('exercise.types.easy'),
        other: t('exercise.types.other')
    };

    const RESISTANCE_LABELS = {
        squat: t('exercise.types.squat'),
        sit_to_stand: t('exercise.types.sit_to_stand'),
        push: t('exercise.types.push'),
        pull: t('exercise.types.pull'),
        arm_raise: t('exercise.types.arm_raise'),
        knee_extension: t('exercise.types.knee_extension')
    };

    const DAYS = [
        { label: t('session.days.sunday'), value: 0 },
        { label: t('session.days.monday'), value: 1 },
        { label: t('session.days.tuesday'), value: 2 },
        { label: t('session.days.wednesday'), value: 3 },
        { label: t('session.days.thursday'), value: 4 },
        { label: t('session.days.friday'), value: 5 },
        { label: t('session.days.saturday'), value: 6 }
    ];

    const renderExercisePrimary = (ex) => {
        if (AEROBIC_TYPES.includes(ex.type)) {
            const label = TYPE_LABELS[ex.type] ?? ex.type;
            const minutes = ex.duration ? Math.round(ex.duration / 60) : 0;

            return `${label} - ${minutes} min`;
        }

        if (RESISTANCE_TYPES.includes(ex.type)) {
            const label = RESISTANCE_LABELS[ex.type] ?? ex.type;
            const sets = ex.sets ?? '-';
            const reps = ex.reps ?? '-';

            return `${label} - ${sets}x${reps}`;
        }

        return ex.type;
    };

    useEffect(() => {
        if (existingSession) {
            setName(existingSession.name || '');
            setDescription(existingSession.description || '');
            setExercises(existingSession.exercises || []);

            if (existingSession.period) {
                const parts = existingSession.period.split(' ');
                if (parts.length >= 5) {
                    setMinute(parts[0]);
                    setHour(parts[1]);
                    const daysArray = parts[4].split(',').map((d) => parseInt(d));
                    setDays(daysArray);
                }
            }
        }
    }, [existingSession]);

    const toggleDay = (day) => {
        setDays((prev) =>
            prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]
        );
    };

    const handleAddExercise = (exercise) => {
        setExercises((prev) => [...prev, { ...exercise, order: prev.length + 1 }]);
        setShowExerciseForm(false);
    };

    const handleDeleteExercise = (id) => {
        setExercises((prev) => prev.filter((ex) => ex.id !== id));
    };

    const handleAddSession = () => {
        let newErrors = {};

        if (!name) newErrors.name = t('errors.session_name');
        if (days.length === 0) newErrors.days = t('errors.select_days');
        if (exercises.length === 0) newErrors.exercises = t('errors.add_exercise');

        setErrors(newErrors);
        if (Object.keys(newErrors).length > 0) return;

        const finalHour = hour ? hour : 0;
        const finalMinute = minute ? minute : 0;
        const period = `${finalMinute} ${finalHour} * * ${days.sort().join(',')}`;

        const session = {
            id: Date.now(),
            name,
            description,
            period,
            exercises
        };

        onAdd(session);

        setName('');
        setDescription('');
        setHour('');
        setMinute('');
        setDays([]);
        setExercises([]);
        setErrors({});
    };

    return (
        <Card
            variant="outlined"
            sx={{
                p: 2,
                borderRadius: 2,
                boxShadow: inlineMode ? 0 : 1,
                backgroundColor: inlineMode ? '#fafafa' : 'white'
            }}
        >
            <CardContent>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                    {t('session.create_title')}
                </Typography>

                <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label={t('session.name_label')}
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            error={!!errors.name}
                            helperText={errors.name}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label={t('session.description_label')}
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                        />
                    </Grid>
                </Grid>

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    {t('session.time_title')}
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label={t('session.hour_label')}
                            type="number"
                            value={hour}
                            onChange={(e) => setHour(e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label={t('session.minute_label')}
                            type="number"
                            value={minute}
                            onChange={(e) => setMinute(e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <FormGroup row>
                            {DAYS.map((d) => (
                                <FormControlLabel
                                    key={d.value}
                                    control={
                                        <Checkbox
                                            checked={days.includes(d.value)}
                                            onChange={() => toggleDay(d.value)}
                                        />
                                    }
                                    label={d.label}
                                />
                            ))}
                        </FormGroup>
                        {errors.days && (
                            <Typography color="error" variant="body2">
                                {errors.days}
                            </Typography>
                        )}
                    </Grid>
                </Grid>

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    {t('session.exercises_title')}
                </Typography>

                {exercises.length === 0 && (
                    <Typography color="text.secondary" sx={{ mb: 1 }}>
                        {t('session.noExercises')}
                    </Typography>
                )}

                {exercises.length > 0 && (
                    <Paper variant="outlined" sx={{ mb: 2 }}>
                        <List dense>
                            {exercises.map((ex, index) => (
                                <ListItem
                                    key={ex.id}
                                    draggable
                                    onDragStart={(e) => {
                                        e.dataTransfer.setData('text/plain', index);
                                        e.currentTarget.style.opacity = '0.4';
                                    }}
                                    onDragEnd={(e) => {
                                        e.currentTarget.style.opacity = '1';
                                        setDragOverIndex(null);
                                    }}
                                    onDragOver={(e) => {
                                        e.preventDefault();
                                        setDragOverIndex(index);
                                    }}
                                    onDragLeave={() => setDragOverIndex(null)}
                                    onDrop={(e) => {
                                        const draggedIndex =
                                            e.dataTransfer.getData('text/plain');
                                        const targetIndex = index;

                                        if (draggedIndex === targetIndex) return;

                                        const updated = [...exercises];
                                        const [moved] = updated.splice(draggedIndex, 1);
                                        updated.splice(targetIndex, 0, moved);

                                        const reordered = updated.map((ex, i) => ({
                                            ...ex,
                                            order: i + 1
                                        }));

                                        setExercises(reordered);
                                        setDragOverIndex(null);
                                    }}
                                    sx={{
                                        cursor: 'grab',
                                        transition: 'background-color 0.2s, border 0.2s',
                                        border:
                                            dragOverIndex === index
                                                ? '2px dashed #1976d2'
                                                : '2px solid transparent',
                                        borderRadius: 1,
                                        backgroundColor:
                                            dragOverIndex === index
                                                ? '#e3f2fd'
                                                : 'transparent',
                                        '&:hover': { backgroundColor: '#f8f8f8' }
                                    }}
                                    secondaryAction={
                                        <IconButton
                                            edge="end"
                                            onClick={() => handleDeleteExercise(ex.id)}
                                            color="error"
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    }
                                >
                                    <ListItemText
                                        primary={`${ex.order}. ${renderExercisePrimary(ex)}`}
                                        secondary={ex.description}
                                    />
                                </ListItem>
                            ))}
                        </List>
                        <Typography
                            variant="caption"
                            sx={{
                                display: 'block',
                                textAlign: 'center',
                                color: 'text.secondary',
                                pb: 1
                            }}
                        >
                            {t('session.drag_help')}
                        </Typography>
                    </Paper>
                )}

                <Box sx={{ textAlign: 'right', mb: 2 }}>
                    <Button
                        variant="outlined"
                        startIcon={
                            showExerciseForm ? (
                                <ExpandLessIcon />
                            ) : (
                                <AddCircleOutlineIcon />
                            )
                        }
                        onClick={() => setShowExerciseForm((prev) => !prev)}
                    >
                        {showExerciseForm
                            ? t('session.close_exercise')
                            : t('session.add_exercise')}
                    </Button>
                </Box>

                <Collapse in={showExerciseForm}>
                    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                        <ExerciseBuilder onAdd={handleAddExercise} hidePeriod />
                    </Paper>
                </Collapse>

                {errors.exercises && (
                    <Typography color="error" variant="body2">
                        {errors.exercises}
                    </Typography>
                )}
            </CardContent>

            <CardActions sx={{ justifyContent: 'flex-end' }}>
                {existingSession ? (
                    <>
                        <Button
                            variant="outlined"
                            color="secondary"
                            onClick={onCancelEdit}
                            sx={{ mr: 1 }}
                        >
                            {t('plans.buttons.cancel')}
                        </Button>
                        <Button
                            variant="contained"
                            color="success"
                            onClick={() => {
                                const finalHour = hour ? hour : 0;
                                const finalMinute = minute ? minute : 0;
                                const period = `${finalMinute} ${finalHour} * * ${days.sort().join(',')}`;

                                onUpdate({
                                    ...existingSession,
                                    name,
                                    description,
                                    exercises,
                                    period
                                });
                            }}
                        >
                            {t('session.save_changes')}
                        </Button>
                    </>
                ) : (
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleAddSession}
                        endIcon={<AddCircleOutlineIcon />}
                    >
                        {t('session.add_session')}
                    </Button>
                )}
            </CardActions>
        </Card>
    );
};

export default SessionBuilder;
